import { redirect, useRouter } from '@tanstack/react-router';
import { normalizeWorkspaceIdRouteParam, type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';
import { RumPageRendered } from '@/services/rum/use-page-rendered';
import ActivityLogsPage from './index.page';
import type { ActivityLogTab } from '@/features/activity-feed';

/**
 * The Profile activity page's URL. It has no `journeys` segment: the old
 * `/journeys/profile_activity/*` and `/journeys/activity_logs/*` URLs are redirect routes to it.
 */
export const PROFILE_ACTIVITY_TAB_ROUTE = '/workspaces/$workspaceId/profile_activity/$tab';

/** Index `beforeLoad`: land on the default (`identified`) tab, keeping the query string. */
export function redirectToDefaultTab(params: { workspaceId?: WorkspaceIdRouteParamInput }): void {
  throw redirect({
    to: PROFILE_ACTIVITY_TAB_ROUTE,
    // The workspace route's params.stringify normalizes the id when the location is built.
    params: { workspaceId: params.workspaceId ?? 'last', tab: 'identified' },
    search: true,
  });
}

type ActivityLogsRouteViewProps = {
  workspaceId: WorkspaceIdRouteParamInput;
  tab: string;
};

/** Renders the Profile activity page for one tab. An unknown tab renders the default tab. */
export function ActivityLogsRouteView({ workspaceId: workspaceIdParam, tab }: ActivityLogsRouteViewProps) {
  const router = useRouter();
  const workspaceId = normalizeWorkspaceIdRouteParam(workspaceIdParam);
  const currentTab: ActivityLogTab = tab === 'anonymous' ? 'anonymous' : 'identified';

  // Build each tab's href, preserving the current filters (search params). `TabNav` renders
  // these as real links and routes them through the app router (client-side nav).
  const tabHref = (next: ActivityLogTab) =>
    router.buildLocation({
      to: PROFILE_ACTIVITY_TAB_ROUTE,
      params: { workspaceId, tab: next },
      search: (prev) => prev,
    }).href;

  return (
    <RumPageRendered>
      <ActivityLogsPage workspaceId={workspaceId} tab={currentTab} tabHref={tabHref} />
    </RumPageRendered>
  );
}
